<template>
    <div class="body-dashboard">
        <section class="body-header">
            <div class="title-subtitle">
                <span class="title">Visão Geral</span>
                <span class="subtitle">Acompanhe toda sua operação, utilize os filtros para visualizar as informações mais importantes do seu negócio.</span>
            </div>
        </section>

        <section class="body-graphic">
            <div
                v-for="(chart, index) in charts"
                :key="index"
                :class="chart.class"
                class="graphic"
            >
                <ui-funnel-graphic
                    v-if="chart.class === 'funnel-graphic'"
                    :dataset="chart.data"
                    :data-percent="chart.percents"
                    :group-icon="chart.icon"
                    :title-text="chart.title"
                    :subtitle-text="chart.subtitle"
                    :loading="chart.loading"
                    :hire-resource="chart.hire_resource"
                    :hire-resource-link="chart.hire_resource_link"
                    :error="chart.error"
                    :no-data-message="chart.no_data_message"
                    :tooltip-link="chart.tooltip_link"
                    :tooltip-text="chart.tooltip_text"
                >
                    <template #action-header>
                        <div class="select-action">
                            <ui-select-v2
                                v-model="filters[chart.type]"
                                :data="chart.filter.options"
                                :disabled="!chart.filter.options.length"
                                :loading="chart.filter.loading"
                                :max-selection="6"
                                :show-apply-button="false"
                                :search="false"
                                return-value="id"
                                placeholder="Etapas"
                                multiple
                                @input="call(chart.filter.handler, $event)"
                            />
                        </div>
                    </template>
                </ui-funnel-graphic>

                <ui-bar-graphic
                    v-else-if="chart.class === 'bar-graphic'"
                    :group-icon="chart.icon"
                    :title-text="chart.title"
                    :subtitle-text="chart.subtitle"
                    :labels="chart.labels"
                    :datasets="chart.data"
                    :loading="chart.loading"
                    :hire-resource="chart.hire_resource"
                    :hire-resource-link="chart.hire_resource_link"
                    :error="chart.error"
                    :no-data-message="chart.no_data_message"
                    :tooltip-link="chart.tooltip_link"
                    :tooltip-text="chart.tooltip_text"
                >
                    <template #action-title>
                        <div class="channels-origins">
                            <span
                                :class="{'channels-active': channelsOrigins.channelsActive}"
                                class="channels"
                                @click="conversionsTypeHandler('channel')"
                            >
                                Canais
                            </span>
                            <span
                                :class="{'origins-active': channelsOrigins.originsActive}"
                                class="origins"
                                @click="conversionsTypeHandler('origin')"
                            >
                                Origens
                            </span>
                        </div>
                    </template>

                    <template #action-header>
                        <div class="select-action">
                            <ui-select-v2
                                v-model="filters[chart.conversion_type]"
                                :data="chart.filter.options"
                                :disabled="!chart.filter.options.length"
                                :loading="chart.filter.loading"
                                :max-selection="7"
                                :placeholder="chart.filter.label"
                                :show-apply-button="false"
                                return-value="id"
                                multiple
                                @input="call(chart.filter.handler, $event)"
                            />
                        </div>
                    </template>
                </ui-bar-graphic>

                <ui-line-graphic
                    v-else-if="chart.class === 'line-graphic'"
                    :group-icon="chart.icon"
                    :title-text="chart.title"
                    :subtitle-text="chart.subtitle"
                    :labels="chart.labels"
                    :datasets="chart.data"
                    :title="chart.data_title"
                    :loading="chart.loading"
                    :hire-resource="chart.hire_resource"
                    :hire-resource-link="chart.hire_resource_link"
                    :error="chart.error"
                    :no-data-message="chart.no_data_message"
                    :tooltip-link="chart.tooltip_link"
                    :tooltip-text="chart.tooltip_text"
                />

                <ui-bar-in-line-graphic
                    v-else-if="chart.class === 'bar-in-line-graphic'"
                    :group-icon="chart.icon"
                    :title-text="chart.title"
                    :subtitle-text="chart.subtitle"
                    :labels="chart.labels"
                    :datasets="chart.data"
                    :loading="chart.loading"
                    :hire-resource="chart.hire_resource"
                    :hire-resource-link="chart.hire_resource_link"
                    :error="chart.error"
                    :no-data-message="chart.no_data_message"
                    :tooltip-link="chart.tooltip_link"
                    :tooltip-text="chart.tooltip_text"
                >
                    <template #action-header>
                        <div class="select-action">
                            <ui-select-v2
                                v-model="filters[chart.type]"
                                :data="chart.filter.options"
                                :disabled="!chart.filter.options.length"
                                :loading="chart.filter.loading"
                                :max-selection="7"
                                :show-apply-button="false"
                                item-label="name"
                                return-value="id"
                                placeholder="Empreendimento"
                                multiple
                                @input="call(chart.filter.handler, $event)"
                            />
                        </div>
                    </template>
                </ui-bar-in-line-graphic>
            </div>
        </section>
    </div>
</template>

<script>
	import { uiSelectV2 } from '@/components/ui/form'
    import {
        uiBarGraphic,
        uiBarInLineGraphic,
        uiFunnelGraphic,
        uiLineGraphic
    } from '@/components/ui/graphics'

    import { dashboard } from '@/services/axios'
    import { storage } from '@/services/storage'
    import { mapState } from 'vuex';

    const moment = require('moment')
    require('moment/locale/pt-br')

    const channelsOrigins = {
        channelsActive: true,
        originsActive: false
    }

    export default {
        name: 'body-dashboard',

        components: {
            uiSelectV2,
            uiBarGraphic,
            uiBarInLineGraphic,
            uiFunnelGraphic,
            uiLineGraphic
        },

        data() {
            return {
                channelsOrigins: { ...channelsOrigins },
                charts: [
                    {
                        type: 'funnel',
                        class: "funnel-graphic",
                        icon: 'fal fa-info-circle',
                        title: 'Funil de Vendas',
                        subtitle: 'Visualize a <strong>taxa de conversão</strong>, o <strong>tempo médio</strong> por cliente e outros indicadores chave do funil de vendas.',
                        all_data: [],
                        sales: 0,
                        data: [],
                        percents: [],
                        loading: false,
                        error: false,
                        no_data_message: 'Não foi possível carregar as informações',
                        hire_resource: false,
                        hire_resource_link: 'https://api.whatsapp.com/send?phone=5511992303372&text=Ol%C3%A1!%20Sou%20cliente%20Facilita%20e%20gostaria%20de%20saber%20como%20contratar%20a%20gest%C3%A3o%20de%20disponibilidade%20e%20reservas.%20Obrigado',
                        tooltip_link: 'https://intercom.help/appfacilita/pt-BR/articles/8412462-como-interpretar-os-dados-apresentados-no-dashboard-inicial',
                        tooltip_text: 'Visão da quantidade de clientes que passaram pelas etapas do funil, média que os clientes permaneceram nas etapas e porcentagem de clientes que passaram para a próxima etapa subsequente.',
                        filter: {
                            options: [],
                            service: '',
                            method: '',
                            handler: 'funnelFilterHandler',
                            parse(data) {
                                return data.filter(option => option.win_probability !== 'won')
                            },
                            loading: false
                        }
                    },
                    {
                        type: 'conversions',
                        conversion_type: 'channel',
                        class: 'bar-graphic',
                        icon: 'fal fa-info-circle',
                        title: 'Conversões',
                        subtitle: 'Visualize os <strong>novos clientes</strong> e <strong>vendas realizadas</strong>, ferramenta crucial para avaliar a efetividade do processo de conversão.',
                        channel_data: [],
                        channel_loaded: false,
                        origin_data: [],
                        origin_loaded: false,
                        data: [],
                        labels: [],
                        loading: false,
                        error: false,
                        no_data_message: 'Não foi possível carregar as informações',
                        hire_resource: false,
                        hire_resource_link: 'https://api.whatsapp.com/send?phone=5511992303372&text=Ol%C3%A1!%20Sou%20cliente%20Facilita%20e%20gostaria%20de%20saber%20como%20contratar%20a%20gest%C3%A3o%20de%20disponibilidade%20e%20reservas.%20Obrigado',
                        tooltip_link: 'https://intercom.help/appfacilita/pt-BR/articles/8412462-como-interpretar-os-dados-apresentados-no-dashboard-inicial',
                        tooltip_text: 'Quantidade de novos clientes em relação as vendas realizadas filtradas pela origem ou canal.',
                        filter: {
                            options: [],
                            service: '',
                            method: '',
                            handler: 'conversionsFilterHandler',
                            label: 'Canais',
                            loading: false
                        }
                    },
                    {
                        type: 'vgv',
                        class: 'line-graphic',
                        icon: 'fal fa-info-circle',
                        title: 'VGV',
                        subtitle: 'Visualize o <strong>Valor Geral de Vendas</strong> semanal, com visão clara do <strong>desempenho</strong> das últimas <strong>4 semanas.</strong>',
                        data: [],
                        labels: [],
                        data_title: 'R$',
                        loading: false,
                        error: false,
                        no_data_message: 'Não foi possível carregar as informações',
                        hire_resource: false,
                        hire_resource_link: 'https://api.whatsapp.com/send?phone=5511992303372&text=Ol%C3%A1!%20Sou%20cliente%20Facilita%20e%20gostaria%20de%20saber%20como%20contratar%20a%20gest%C3%A3o%20de%20disponibilidade%20e%20reservas.%20Obrigado',
                        tooltip_link: 'https://intercom.help/appfacilita/pt-BR/articles/8412462-como-interpretar-os-dados-apresentados-no-dashboard-inicial',
                        tooltip_text: 'Soma do campo valor em dados da unidade, de todos os clientes na etapa de venda ganha e com a data da venda dentro dos últimos 30 dias filtrados em períodos de 7 dias.'
                    },
                    {
                        type: 'availability',
                        class: 'bar-in-line-graphic',
                        icon: 'fal fa-info-circle',
                        title: 'Disponibilidade',
                        subtitle: 'Visualize o status de unidades dos empreendimentos, ilustrando claramente a disponibilidade e reservas.',
                        all_data: [],
                        data: [],
                        labels: [],
                        loading: false,
                        error: false,
                        no_data_message: 'Não foi possível carregar as informações. Tente novamente mais tarde.',
                        hire_resource: false,
                        hire_resource_link: 'https://api.whatsapp.com/send?phone=5511992303372&text=Ol%C3%A1!%20Sou%20cliente%20Facilita%20e%20gostaria%20de%20saber%20como%20contratar%20a%20gest%C3%A3o%20de%20disponibilidade%20e%20reservas.%20Obrigado',
                        tooltip_link: 'https://intercom.help/appfacilita/pt-BR/articles/8412462-como-interpretar-os-dados-apresentados-no-dashboard-inicial',
                        tooltip_text: 'Visão dos status atuais das unidades filtradas por empreendimento.',
                        filter: {
                            options: [],
                            service: '',
                            method: '',
                            handler: 'availabilityFilterHandler',
                            loading: false
                        }
                    }
                ],
                filters: {},
                availability_status: {
                    available: {
                        label: 'Disponível',
                        color: "rgba(26, 209, 137, 1)"
                    },
                    reserved: {
                        label: 'Reservado',
                        color: 'rgba(255, 153, 0, 1)'
                    },
                    'pre-reserve': {
                        label: 'Pré-reserva',
                        color: 'rgba(254, 206, 36, 1)'
                    },
                    'reserved-permanetly': {
                        label: 'Reserva Permanente',
                        color: '#939393'
                    },
                    sold: {
                        label: 'Vendido',
                        color: 'rgba(255, 86, 123, 1)'
                    },
                    sale: {
                        label: 'Vendido',
                        color: 'rgba(255, 86, 123, 1)'
                    },
                    canceled: {
                        label: 'Cancelado',
                        color: '#939393'
                    },
                    unavailable: {
                        label: 'Indisponível',
                        color: '#9B59B6'
                    },
                    waiting: {
                        label: 'Em Análise',
                        color: '#FFD755'
                    }
                },
                configurations: {
                    allowed: []
                }
            }
        },

        mounted() {
            // Verifica quais gráficos serão mostrados, de acordo com os recursos contratados
            const allowedMenuConfig = storage.get('allowed_menu')
            this.configurations.allowed = allowedMenuConfig.includes('empreendimentos')
                ? ['availability']
                : []
            if (allowedMenuConfig.includes('clientes')) {
                this.configurations.allowed = this.configurations.allowed.concat(['vgv', 'funnel', 'conversions'])
            }

            // Verifica se a seleção do tipo de conversão foi salva no localStorage
            this.conversionsTypeHandler(
                storage.get('dashboard_conversions_type') || 'channel',
                false
            )

            this.loadData()
        },

        methods: {
			/**
			 * Chama dinamicamente um handler para tratar o evento do filtro
			 */
			call(handler, event) {
                storage.set('dashboard_filters', JSON.stringify(this.filters))
				if (handler) this[handler](event)
			},
            /**
             * Carrega os dados necessários para a filtragem das informações no gráfico
             */
            loadFilter(chart) {
                chart.filter.options = []
                if (chart.filter.service && chart.filter.method) {
                    (() => import('@/services/axios/modules/'+ chart.filter.service + '.js'))().then(service => {
                        const api = service.default.build()
                        let params = ['']
                        if (chart.filter.params) params = [...chart.filter.params]

                        chart.filter.loading = true
                        api[chart.filter.method](...params)
                            .then(response => {
                                let data = []
                                /** Verifica se a requisição retornou uma paginação */
                                if (Object.prototype.hasOwnProperty.call(response.data, 'total')) {
                                    data = [...response.data.data]

                                /** Verifica se a requisição retornou um array */
                                } else if (Object.prototype.hasOwnProperty.call(response.data, 'length')) {
                                    data = [...response.data]

                                /** Se nenhum dos outros casos for atendido, trata o retorno como um objeto */
                                } else if (typeof response.data === 'object') {
                                    data = response.data.data ? [...response.data.data] : { ...response.data }
                                }

                                /** Verifica se os resultados necessitam ser formatados */
                                chart.filter.options = chart.filter.parse
                                    ? chart.filter.parse(data)
                                    : Object.values(data)

                                if (chart.type === 'funnel') {
                                    const winOption = chart.filter.options.find(option => option.win_probability === 'won')
                                    this.filters.funnel = winOption ? [winOption.id] : []
                                }

                                if (chart.filter.handler) {
                                    const filter = chart.type === 'conversions' ? chart.conversion_type : chart.type
                                    this.call(chart.filter.handler, this.filters[filter])
                                }
                            })
                            .finally(() => {
                                chart.filter.loading = false
                            })
                    })
                }
            },
            loadChartData: async function (chart) {
                // Verifica se o recurso ao qual o gráfico pertence foi contratado
                if (!this.configurations.allowed.includes(chart.type)) {
                        chart.no_data_message = 'Este recurso não foi contratado! Acesse o link abaixo para contratá-lo.'
                        chart.hire_resource = true
                        chart.error = true
                        return;
                    }

                    const params = {
                        type: chart.type === 'conversions' ? chart.conversion_type : chart.type
                    }

                    chart.loading = true
                    // Verifica se o gráfico possui um filtro
                    if (chart.filter?.handler) chart.filter.loading = true
                    try {

                        const filters = {
                            ...(this.appliedFilters.dateRange?.initialDate && { startDate: this.appliedFilters.dateRange.initialDate }),
                            ...(this.appliedFilters.dateRange?.finalDate && { endDate: this.appliedFilters.dateRange.finalDate }),
                            ...(this.appliedFilters.teamFilter.team?.length > 0 && {
                                teams: this.appliedFilters.teamFilter.team.map((selected) => selected.id),
                            }),
                            ...(this.appliedFilters.teamFilter.manager?.length > 0 && {
                                managers: this.appliedFilters.teamFilter.manager.map((selected) => selected.id),
                            }),
                            ...(this.appliedFilters.teamFilter.broker?.length > 0 && {
                                brokers: this.appliedFilters.teamFilter.broker.map((selected) => selected.id),
                            }),
                        };

                        let response = await dashboard.createV2('/charts', {
                                ...params, 
                                "filters": filters
                            })

                        switch (chart.type) {
                            case 'funnel':
                                chart.all_data = Object.values(response.data?.data?.data || [])
                                    .map(item => {
                                        return {
                                            id: item.funnel_stage_id,
                                            position: item.position,
                                            win_probability: item.win_probability || 0,
                                            label: item.name,
                                            value: item.count,
                                            day: !['won', 'lost'].includes(item.win_probability)
                                                ? `${Math.ceil(item.avg_period)} dia(s)`
                                                : null
                                        }
                                    })

                                const formatComparativeDate = (date) => {
                                    return moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD')
                                }

                                // Busca os dados referentes a etapa de Venda Ganha
                                response = await dashboard.createV2('/cards', { type: 'sales', filters: {
                                    ...filters,
                                    ...(this.appliedFilters.comparativeDate?.finalDate && {
                                        dateComparation: `${formatComparativeDate(this.appliedFilters.comparativeDate.initialDate)} ${formatComparativeDate(this.appliedFilters.comparativeDate.finalDate)}`,
                                    }), 
                                } })
                                chart.sales = response.data?.data?.value || 0
                                break

                            case 'conversions':
                                chart[`${chart.conversion_type}_data`] = Object.values(response.data?.data?.data || [])
                                chart[`${chart.conversion_type}_loaded`] = true
                                break

                            case 'vgv':
                                chart.data = [
                                    {
                                        label: ["Visualize o Valor Geral de Vendas semanal, com visão clara do desempenho das últimas 4 semanas."],
                                        data: Object.values(response.data?.data?.data || [])
                                            .sort((a, b) => {
                                                // Ordena pelas datas nos labels
                                                let aDate = a.label.split('/')
                                                let bDate = b.label.split('/')

                                                return parseInt(`${aDate[1]}${aDate[0]}`) - parseInt(`${bDate[1]}${bDate[0]}`)
                                            })
                                            .map(item => parseFloat(item.count)),
                                        fill: false,
                                        borderWidth: 2,
                                        borderDash: [5, 5],
                                        borderColor: '#3C5D75',
                                        pointBackgroundColor: 'rgba(255, 7, 62, 0.53)',
                                        pointBorderColor: 'rgba(255, 7, 62, 0.53)',
                                        pointBorderWidth: 1,
                                        pointRadius: 6
                                    },
                                ]

                                // Pega os labels ordenados
                                chart.labels = Object.values(response.data?.data?.data || [])
                                    .sort((a, b) => {
                                        // Ordena pelas datas nos labels
                                        let aDate = a.label.split('/')
                                        let bDate = b.label.split('/')

                                        return parseInt(`${aDate[1]}${aDate[0]}`) - parseInt(`${bDate[1]}${bDate[0]}`)
                                    })
                                    .map(item => item.label)
                                break

                            case 'availability':
                                chart.all_data = Object.values(response.data?.data?.data || [])
                                    .map(item => {
                                        /**
                                         * Gera um objeto JSON, utilizando como chave a propriedade situation e como valor
                                         * a propriedade ammount
                                         */
                                        item.availability_summary = Object.fromEntries(
                                            Object.values(item.availability_summary)
                                                .map(summary => [summary.situation, summary.ammount])
                                        )

                                        return item
                                    })
                                break
                        }

                        // Verifica se é necessário carregar os dados de filtragem
                        if (chart.filter?.service && chart.filter?.method) {
                            this.loadFilter(chart)

                        } else if (chart.filter?.handler) {
                            if (['availability', 'funnel'].includes(chart.type)) {
                                chart.filter.options = chart.filter.parse
                                    ? chart.filter.parse(chart.all_data)
                                    : Object.values(chart.all_data)

                            } else if (chart.type === 'conversions') {
                                const type = chart.conversion_type
                                chart.filter.options = chart[`${type}_data`].map(item => {
                                    return { id: item[`${type}_id`], label: item.label }
                                })
                            }

                            chart.filter.loading = false
                            const filter = chart.type === 'conversions' ? chart.conversion_type : chart.type
                            this.call(chart.filter.handler, this.filters[filter])
                        }

                    } catch (error) {
                        if (error.data && error.data.msg) this.$toastr.e(error.data.msg)
                        chart.error = true

                    } finally {
                        chart.loading = false
                        if (chart.filter?.handler) chart.filter.loading = false;
                    }
            },
            /**
             * Carrega os dados necessários para os gráficos
             */
            loadData: async function () {
                this.$store.commit('dashboardFilter/SET_LOADING', true);
                // Carrega as informações dos filtros à partir do localStorage, caso tenham sido salvas
                const filters = JSON.parse(storage.get('dashboard_filters'))
                if (filters) this.filters = { ...filters }

                // Inicia o carregamento dos dados para cada gráfico sem aguardar
                this.charts.forEach((chart) => {
                    this.loadChartData(chart);
                });

                this.$store.commit('dashboardFilter/SET_LOADING', false);
            },
            /**
             * Função que filtra os dados das etapas do funil, de acordo com a seleção do
             * filtro
             */
            funnelFilterHandler() {
                this.$gtm.trackEvent({
                    event: "fac_button_click",
                    category: 'vue-dashboard',
                    action: "click",
                    label: 'Etapas do Funil',
                })

                let chart = this.charts.find(chart => chart.type === 'funnel')
                // Aplica a filtragem nos dados listados
                chart.data = this.filters.funnel?.length
                    ? chart.all_data
                        .filter(item => this.filters.funnel.includes(item.id))
                        .sort((a,b) => a.position - b.position)
                    : chart.all_data
                        .filter(option => option.win_probability !== 'won')
                        .sort((a,b) => a.position - b.position)
                        .slice(0, 6)

                let saleStage = chart.all_data.find(item => item.win_probability === 'won')
                if (saleStage) {
                    saleStage.value = chart.sales || 0
                    chart.data.push(saleStage)
                }

                // Monta o array contendo os percentuais do gráfico
                chart.percents = []
                for (const index in chart.data) {
                    if (parseInt(index) === 0) continue

                    chart.percents.push({
                        percent: chart.data[index-1].value
                            ? `${((chart.data[index].value / chart.data[index-1].value) * 100)
                                    .toFixed(2)
                                    .toString()
                                    .replace(/\./g, ",")
                                }%`
                            : '0%'
                    })
                }
            },
            /**
             * Event handler que trata a seleção do tipo de conversão a ser mostrada no relatório,
             * e carrega as informações necessárias
             *
             * @params type Tipo de conversão a ser mostrada
             * @params allowLoad Define se será necessário carregar os dados caso estes ainda não
             * tenham sido carregados
             */
            conversionsTypeHandler: async function (type, load = true) {
                // Verifica se o recurso foi contratado
                if (!this.configurations.allowed.includes('conversions')) return

                let chart = this.charts.find(chart => chart.type === 'conversions')
                chart.conversion_type = type
                chart.filter.label = type === 'channel' ? 'Canais' : 'Origens'
                chart.filter.loading = true
                storage.set('dashboard_conversions_type', type)
                this.channelsOrigins.channelsActive = type === 'channel'
                this.channelsOrigins.originsActive = type === 'origin'

                // Emite o evento do Google Analytics
                this.$gtm.trackEvent({
                    event: "fac_button_click",
                    category: 'vue-dashboard',
                    action: "click",
                    label: chart.filter.label,
                })

                // Verifica se a primeira requisição para o tipo de conversão foi feita
                if (!chart[`${type}_loaded`] && load) {
                    try {
                        // Prepara os dados a serem enviados na requisição
                        const params = { type: chart.conversion_type }

                        // Carrega os dados do relatório de acordo com o tipo de conversão
                        chart.loading = true
                        const response = await dashboard.createV2('/charts', params)
                        chart[`${type}_data`] = Object.values(response.data?.data?.data || [])
                        chart[`${type}_loaded`] = true

                    } catch (error) {
                        if (error.data && error.data.msg) this.$toastr.e(error.data.msg)
                        console.log(error)

                    } finally {
                        chart.loading = false
                    }

                }

                // Carrega os dados de filtragem e filtra os dados
                chart.filter.options = chart[`${type}_data`].map(item => {
                    return { id: item[`${type}_id`], label: item.label }
                })
                chart.filter.loading = false

                this.conversionsFilterHandler()
            },
            /**
             * Função que filtra os dados das conversões, de acordo com a seleção do
             * filtro e do tipo de conversão selecionado
             */
            conversionsFilterHandler() {
                let chart = this.charts.find(chart => chart.type === 'conversions')

                // Emite o evento do Google Analytics
                this.$gtm.trackEvent({
                    event: "fac_button_click",
                    category: 'vue-dashboard',
                    action: "click",
                    label: `Filtrar ${chart.filter.label}`,
                })

                /**
                 * Aplica o filtro sobre os dados listados. Caso não tenha sido selecionado um filtro,
                 * mostra os dados dos últimos 7 empreendimentos cadastrados.
                 */
                const filtered = this.filters[chart.conversion_type]?.length
                    ? chart[`${chart.conversion_type}_data`].filter(item => {
                        return this.filters[chart.conversion_type].includes(item.channel_id || item.origin_id)
                    })
                    : chart[`${chart.conversion_type}_data`]
                        .sort((a, b) => moment(a.created_at).format('YYYYMMDDHHmmss') - moment(b.created_at).format('YYYYMMDDHHmmss'))
                        .slice(0, 7)

                chart.data = [
                    {
                        label: ["Novos clientes"],
                        data: filtered.map(item => item.total),
                        backgroundColor: "#33a7ff", // Cor de fundo das barras
                        borderWidth: 1,
                        borderRadius: 5
                    },
                    {
                        label: ["Vendas realizadas"],
                        data: filtered.map(item => item.saled),
                        backgroundColor: "#49d49d", // Cor de fundo das barras
                        borderWidth: 1,
                        borderRadius: 5
                    }
                ]
                chart.labels = filtered.map(item => item.label)
            },
            /**
             * Função que filtra os dados das disponibilidades, de acordo com a seleção do
             * filtro
             */
            availabilityFilterHandler() {
                // Emite o evento do Google Analytics
                this.$gtm.trackEvent({
                    event: "fac_button_click",
                    category: 'vue-dashboard',
                    action: "click",
                    label: 'Filtrar Disponibilidade',
                })

                let chart = this.charts.find(chart => chart.type === 'availability')
                /**
                 * Aplica o filtro sobre os dados listados. Caso não tenha sido selecionado um filtro,
                 * mostra os dados dos últimos 7 empreendimentos cadastrados.
                 */
                const filtered = this.filters[chart.type]?.length
                    ? chart.all_data.filter(item =>this.filters[chart.type].includes(item.id))
                    : chart.all_data
                        .sort((a, b) => moment(a.created_at).format('YYYYMMDD') - moment(b.created_at).format('YYYYMMDD'))
                        .slice(0, 7)

                // Busca todos os status que possuem alguma unidade cadastrada para os produtos filtrados
                const onlyUnique = (value, index, array) => array.indexOf(value) === index
                const usedStatus = filtered.map(item => Object.keys(item.availability_summary))
                    .reduce((acc, array) => acc.concat(array), [])
                    .filter(onlyUnique)

                chart.data = usedStatus.map(status => {
                    return {
                        label: [this.availability_status[status].label],
                        data: filtered.map(item => item.availability_summary[status] || 0),
                        backgroundColor: this.availability_status[status].color, // Cor de fundo das barras
                        borderRadius: 5
                    }
                })
                chart.labels = filtered.map(item => item.name)
            },
            /**
             * 
             * Essa função checa se as variaveis necessárias que precisam mudar estao mudando para
             * chamar o endpoint carts, a variável dateComparation hoje chama apenas o endpoint card
             */
            shouldLoadData(newVal, oldVal) {
                const timePeriodChanged = newVal.timePeriod !== oldVal.timePeriod;

                const dateRangeChanged = !this.objectsEqual(newVal.dateRange, oldVal.dateRange);

                const teamFilterChanged = !this.objectsEqual(newVal.teamFilter, oldVal.teamFilter);

                return timePeriodChanged || dateRangeChanged || teamFilterChanged;
            },
            objectsEqual(obj1, obj2) {
                return JSON.stringify(obj1) === JSON.stringify(obj2);
            }

        },

        computed: {
            ...mapState('dashboardFilter', ['appliedFilters']),               
        },

        watch: {
            appliedFilters: {
                handler(newVal, oldVal) {
                    if (this.shouldLoadData(newVal, oldVal)) {
                        this.loadData();
                    }
                },
                deep: true
            }
        }
    }
</script>

<style lang="stylus" scoped>
	@import 'styles.styl';
</style>